/**
 * useQueueBadgeCount — Lightweight hook that just tracks total pending queue count.
 *
 * Designed for sidebar badge display. Uses localStorage + periodic fetch
 * to avoid heavy API calls on every page.
 */

import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

const CACHE_KEY = 'queue-badge-count';
const CACHE_TTL = 60_000; // 1 minute cache
const POLL_INTERVAL = 60_000; // check every 60s
// Coalescing window for realtime bursts. Each queue change used to schedule its
// own independent refresh, so N changes produced N refreshes and 2N HEAD
// requests (fetchCount issues two sequential counts).
const REALTIME_DEBOUNCE = 750;

interface CachedCount {
  count: number;
  criticalCount: number;
  timestamp: number;
}

export function useQueueBadgeCount(): { count: number; criticalCount: number } {
  const [count, setCount] = useState<number>(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed: CachedCount = JSON.parse(cached);
        if (Date.now() - parsed.timestamp < CACHE_TTL) {
          return parsed.count;
        }
      }
    } catch { /* ignore */ }
    return 0;
  });

  const [criticalCount, setCriticalCount] = useState<number>(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed: CachedCount = JSON.parse(cached);
        if (Date.now() - parsed.timestamp < CACHE_TTL) {
          return parsed.criticalCount;
        }
      }
    } catch { /* ignore */ }
    return 0;
  });

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const fetchCount = async () => {
      try {
        // Quick count from sys_automation_queue (local DB, fast)
        const { count: totalCount, error } = await supabase
          .from('sys_automation_queue')
          .select('*', { count: 'exact', head: true })
          .in('status', ['pending', 'in_progress', 'needs_attention']);

        if (!error && totalCount !== null && mountedRef.current) {
          // Also count critical items
          const { count: critical } = await supabase
            .from('sys_automation_queue')
            .select('*', { count: 'exact', head: true })
            .eq('priority', 'critical')
            .in('status', ['pending', 'needs_attention']);

          const c = critical ?? 0;
          setCount(totalCount);
          setCriticalCount(c);

          try {
            localStorage.setItem(CACHE_KEY, JSON.stringify({
              count: totalCount,
              criticalCount: c,
              timestamp: Date.now(),
            } as CachedCount));
          } catch { /* ignore */ }
        }
      } catch {
        // Silent fail for badge — not critical
      }
    };

    fetchCount();
    const timer = setInterval(fetchCount, POLL_INTERVAL);

    // Coalesce realtime bursts into a single trailing refresh.
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleRefresh = () => {
      if (!mountedRef.current) return;
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        refreshTimer = null;
        if (mountedRef.current) fetchCount();
      }, REALTIME_DEBOUNCE);
    };

    // Realtime subscription for immediate updates
    const channel = supabase
      .channel('badge-count')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'sys_automation_queue',
      }, scheduleRefresh)
      .subscribe();

    return () => {
      mountedRef.current = false;
      clearInterval(timer);
      if (refreshTimer) clearTimeout(refreshTimer);
      channel.unsubscribe();
    };
  }, []);

  return { count, criticalCount };
}
